import mixpanel from "mixpanel-browser";
import type { MySelf } from "./utils";

export function initAnalytics(token: string) {
  if (!token) return;
  mixpanel.init(token, {
    autocapture: true,
    record_sessions_percent: 0,
    api_host: "/api/mp",
  });
}

export function identifyUser(user: MySelf) {
  mixpanel.identify(String(user.id));
  mixpanel.people.set({
    $name: user.name,
    $email: user.email,
    tier: user.userTier,
    is_staff: user.isStaff,
    // Spread conditionally: an unanswered survey must leave the property absent rather
    // than set it to null, so "base" and "we never asked" stay tellable apart in Mixpanel.
    ...(user.cluster ? { cluster: user.cluster } : {}),
  });
}

export function resetAnalyticsUser() {
  mixpanel.reset();
}

type AnalyticsEvent =
  | { name: "series_created"; props: { series_id: number } }
  | { name: "series_updated"; props: { series_id: number } }
  | { name: "series_deleted"; props: { series_id: number } }
  | { name: "series_detail_opened"; props: { series_id: number } }
  | { name: "episode_created"; props: { series_id: number } }
  | { name: "episode_updated"; props: { episode_id: number } }
  | {
      name: "episode_deleted";
      props: { episode_id: number; series_id: number };
    }
  | {
      name: "invitation_created";
      props: { series_id: number; role: string; expires_in: string };
    }
  | {
      name: "invitation_revoked";
      props: { series_id: number; invitation_id: number };
    }
  | {
      name: "assignment_role_changed";
      props: { series_id: number; new_role: string };
    }
  | { name: "assignment_removed"; props: { series_id: number } }
  | {
      name: "episode_editor_opened";
      props: { episode_id: number; series_id: number };
    }
  | {
      name: "episode_validation_started";
      props: { episode_id: number; series_id: number };
    }
  | {
      name: "episode_playback_started";
      props: { episode_id: number; series_id: number };
    }
  | { name: "task_started"; props: { episode_id: number; task_name: string } }
  | { name: "task_terminated"; props: { task_id: string } }
  | { name: "bundle_created"; props: { episode_id: number } }
  | {
      name: "bundle_deleted";
      props: { episode_id: number; bundle_id: number };
    }
  | {
      name: "bundle_published_beta";
      props: { episode_id: number; bundle_id: number };
    }
  | {
      name: "bundle_published_official";
      props: {
        episode_id: number;
        bundle_id: number;
        /** -1 when no calendar slot was picked (immediate or scheduled). */
        slot_id: number;
        target: "slot" | "immediate" | "scheduled" | "patch";
      };
    }
  | {
      name: "publication_unpublished";
      props: { episode_id: number; publication_id: number };
    }
  | { name: "user_signed_out"; props: Record<string, never> }
  | { name: "audio_override_created"; props: { episode_id: string } }
  | { name: "speech_text_override_created"; props: { episode_id: string } }
  | {
      name: "dictionary_word_added";
      props: { series_id: number; word: string; source: "button" | "modal" };
    }
  | { name: "dictionary_word_removed"; props: { series_id: number } }
  | { name: "dictionary_opened"; props: { series_id: number } }
  | {
      name: "user_survey_submitted";
      props: {
        source: "auto" | "menu";
        cluster: "base" | "pro";
        audience: "individual" | "organisation";
        is_author: boolean;
        wrote_interactive_fiction: boolean | null;
        genre_count: number;
        published_on_count: number;
      };
    }
  | {
      /** The number that matters for a prompt with no snooze cap. */
      name: "user_survey_snoozed";
      props: { source: "auto" | "menu"; snooze_count: number };
    }
  | {
      name: "react_error";
      props: {
        kind: "uncaught" | "caught" | "recoverable";
        message: string;
        stack?: string;
        component_stack?: string;
      };
    };

export function track<E extends AnalyticsEvent>(
  event: E["name"],
  props: Extract<AnalyticsEvent, { name: E["name"] }>["props"],
) {
  mixpanel.track(event, props);
}
